import { PiCubeBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

const FALLBACK_IMG = '/img/favicon.png';

/** Tamanho universal da foto na grade de produtos (px). */
export const PRODUCT_GRID_IMAGE_PX = 200;

/** Tamanho da foto no detalhe de consulta de estoque (px). */
export const PRODUCT_DETAIL_IMAGE_PX = 400;

type Props = {
    src: string | null | undefined;
    alt?: string;
    /** Grade: 200×200; lista: miniatura; detail: 400×400. */
    size?: 'grid' | 'list' | 'detail';
    className?: string;
};

/**
 * Quadro CSS fixo por tamanho — grade 200×200, detalhe 400×400.
 */
export default function ProductThumbnail({ src, alt = '', size = 'grid', className }: Props) {
    if (size === 'list') {
        return (
            <div
                className={cn(
                    'relative size-12 shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-900',
                    className,
                )}
            >
                {src ? (
                    <img
                        src={src}
                        alt={alt}
                        width={48}
                        height={48}
                        className="absolute inset-0 size-full object-contain object-center p-1"
                        loading="lazy"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = FALLBACK_IMG;
                        }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                        <PiCubeBold className="size-6 text-gray-300 dark:text-gray-600" />
                    </div>
                )}
            </div>
        );
    }

    const px = size === 'detail' ? PRODUCT_DETAIL_IMAGE_PX : PRODUCT_GRID_IMAGE_PX;

    return (
        <div
            className={cn(
                'flex shrink-0 items-center justify-center bg-white dark:bg-gray-900',
                size === 'detail'
                    ? 'overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700'
                    : 'w-full',
                className,
            )}
            style={{
                width: size === 'detail' ? px : undefined,
                height: px,
                minHeight: px,
                maxWidth: '100%',
            }}
        >
            <div
                className="relative shrink-0 overflow-hidden bg-white dark:bg-gray-900"
                style={{
                    width: size === 'detail' ? '100%' : `min(${px}px, 100%)`,
                    aspectRatio: '1 / 1',
                    height: size === 'detail' ? '100%' : 'auto',
                }}
            >
                {src ? (
                    <img
                        src={src}
                        alt={alt}
                        width={px}
                        height={px}
                        className="absolute inset-0 size-full object-contain object-center"
                        loading="lazy"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = FALLBACK_IMG;
                        }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                        <PiCubeBold
                            className={cn(
                                'text-gray-300 dark:text-gray-600',
                                size === 'detail' ? 'size-20' : 'size-14',
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
